import { Form } from "antd";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";

import { PrimaryButton } from "../../../components/Buttons/components/ActionButton";
import { TextInput } from "../../../components/Form/formComponents";
import { FormFieldLabelStyle, VerticalFormFieldStyleDiv } from "../../../components/Form/formFieldLabelPositioning/styles/FormFieldsStyle";
import { ActionLink } from "../../../components/Link";
import { Logo, Logos, LogoSize } from "../../../components/Logo/Logo";
import { NotificationRouteListener, showErrorNotification } from "../../../components/NotificationAlerts";
import { NotificationAlertManager } from "../../../components/NotificationAlerts/NotificationAlertManager";
import { RedirectProvider } from "../../../core/router/auth/RedirectProvider";
import { useLoginMutation } from "../../../core/router/auth/serverApi";
import { routes } from "../../../core/router/config/routes";
import type { NoneOwnProps } from "../../../core/types";
import { PasswordInput } from "./PasswordInput";
import { ConfirmationButtonWrapper, FooterWrapper, LogoWrapper, StyledDiv, TitleWrapper } from "./styles/LoginStyle";
import { TitleLogin } from "./styles/TitleLoginStyle";

export const LoginPage: FC<NoneOwnProps> = () => {
    const [form] = Form.useForm();
    const [loginRequest, { isLoading }] = useLoginMutation();
    const { t } = useTranslation();
    const [showPasswordInput, setShowPasswordInput] = useState(false);

    const USER_NAME = "username";
    const PASSWORD = "password";

    const handleFinish = () => {
        const username: string = form.getFieldValue(USER_NAME);
        const password: string = form.getFieldValue(PASSWORD);
        loginRequest({ username, password });
    };

    const handleKeyDown = (ev: { key: string }) => {
        if (ev.key === "Enter") {
            if (!showPasswordInput) {
                togglePasswordInput();
            } else {
                handleFinish();
            }
        }
    };

    const togglePasswordInput = () => {
        form.validateFields([USER_NAME])
            .then(() => setShowPasswordInput(!showPasswordInput))
            .catch((err) => showErrorNotification(err));
    };

    return (
        <StyledDiv data-name="login_form">
            <RedirectProvider />
            <NotificationRouteListener />
            <NotificationAlertManager />
            <LogoWrapper>
                <Logo src={Logos.AeviLogoRebrand} size={LogoSize.REGULAR} />
            </LogoWrapper>
            <TitleWrapper>
                <TitleLogin>{t("page.title.login")}</TitleLogin>
            </TitleWrapper>
            <Form form={form} initialValues={{ remember: true }} onFinish={handleFinish} onKeyDown={handleKeyDown} autoComplete="off">
                {!showPasswordInput && (
                    <>
                        <VerticalFormFieldStyleDiv>
                            <FormFieldLabelStyle>{t("login.nickname")}</FormFieldLabelStyle>
                            <Form.Item
                                key={`${USER_NAME}FormItem`}
                                name={USER_NAME}
                                rules={[{ required: true, message: "Please input your username!" }]}
                            >
                                <TextInput />
                            </Form.Item>
                        </VerticalFormFieldStyleDiv>
                        <ConfirmationButtonWrapper>
                            <PrimaryButton name="continue" onClick={togglePasswordInput}>
                                {t("common.continue")}
                            </PrimaryButton>
                        </ConfirmationButtonWrapper>
                    </>
                )}
                {showPasswordInput && (
                    <>
                        <VerticalFormFieldStyleDiv>
                            <FormFieldLabelStyle>Password</FormFieldLabelStyle>
                            <Form.Item
                                key={`${PASSWORD}FormItem`}
                                name={PASSWORD}
                                rules={[{ required: true, message: "Please input your password!" }]}
                            >
                                <PasswordInput />
                            </Form.Item>
                        </VerticalFormFieldStyleDiv>
                        <ConfirmationButtonWrapper>
                            <PrimaryButton name="submit" htmlType="submit" loading={isLoading}>
                                {t("button.submit")}
                            </PrimaryButton>
                        </ConfirmationButtonWrapper>
                    </>
                )}
            </Form>
            <FooterWrapper>
                <ActionLink route={routes.login} to="https://idm.dev.eu.aevi-test.com/auth/forgotten-password">
                    {t("login.forgotPassword")}
                </ActionLink>
            </FooterWrapper>
        </StyledDiv>
    );
};
